import apiService from './api';
import type {
  BLOCK_VIEW,
  CalendarEventOccurrence,
  CalendarEventsFilters,
  ChurchCMSBlock,
  ChurchImage,
  ContactFormRequestPayload,
  FacebookPageData,
  GroupAdminAccess,
  SuggestedModule,
  UpdateCMSBlockPayload,
  UploadFileResponse,
} from '@/types';
import { formatQueryString } from '@/helpers';

const API_URL = '/admin/church';

class ChurchService {
  getSuggestedModules = async (churchId: string) => {
    try {
      const response = await apiService.get<SuggestedModule[]>(`${API_URL}/${churchId}/suggested-modules`);
      return response.data;
    } catch (e) {
      console.error('Error fetching suggested modules:', e);
      throw e;
    }
  };

  activateSuggestedModule = async (churchId: string, id: string) => {
    try {
      const response = await apiService.post<void>(`${API_URL}/${churchId}/suggested-modules/${id}/activate`, {});
      return response.data;
    } catch (e) {
      console.error('Error activating suggested module:', e);
      throw e;
    }
  };

  deactivateSuggestedModule = async (churchId: string, id: string) => {
    try {
      const response = await apiService.delete<void>(`${API_URL}/${churchId}/suggested-modules/${id}/deactivate`);
      return response.data;
    } catch (e) {
      console.error('Error deactivating suggested module:', e);
      throw e;
    }
  };

  getChurchCMSBlocks = async (id: string) => {
    try {
      const response = await apiService.get<{ items: ChurchCMSBlock[] }>(`${API_URL}/${id}/cms-blocks`);
      return response.data;
    } catch (e) {
      console.error('Error fetching church CMS blocks:', e);
      throw e;
    }
  };

  updateChurchCMSBlocks = async (id: string, data: UpdateCMSBlockPayload) => {
    try {
      const response = await apiService.put<{ items: ChurchCMSBlock[] }>(`${API_URL}/${id}/cms-blocks`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating church CMS blocks:', e);
      throw e;
    }
  };

  getCustomCMSBlocks = async (id: string) => {
    try {
      const response = await apiService.get<ChurchCMSBlock[]>(`${API_URL}/${id}/custom-modules`);
      return response.data;
    } catch (e) {
      console.error('Error fetching custom CMS blocks:', e);
      throw e;
    }
  };

  createChurchCMSBlocks = async (id: string, data: ChurchCMSBlock) => {
    try {
      const response = await apiService.post<{ items: ChurchCMSBlock[] }>(`${API_URL}/${id}/custom-modules`, data);
      return response.data;
    } catch (e) {
      console.error('Error creating church CMS blocks:', e);
      throw e;
    }
  };

  updateCustomModule = async (churchId: string, id: string, data: ChurchCMSBlock) => {
    try {
      const response = await apiService.put<{ items: ChurchCMSBlock[] }>(
        `${API_URL}/${churchId}/custom-modules/${id}`,
        data
      );
      return response.data;
    } catch (e) {
      console.error('Error updating custom module:', e);
      throw e;
    }
  };

  updateAllCustomModules = async (churchId: string, data: UpdateCMSBlockPayload) => {
    try {
      const response = await apiService.put<{ items: ChurchCMSBlock[] }>(`${API_URL}/${churchId}/custom-modules`, data);
      return response.data;
    } catch (e) {
      console.error('Error updating all custom modules:', e);
      throw e;
    }
  };

  changeCustomModuleViewType = async (churchId: string, id: string, viewType: BLOCK_VIEW) => {
    try {
      const response = await apiService.put<{ items: ChurchCMSBlock[] }>(
        `${API_URL}/${churchId}/custom-modules/${id}/change-view-type`,
        { viewType }
      );
      return response.data;
    } catch (e) {
      console.error('Error changing custom module view type:', e);
      throw e;
    }
  };

  enableCustomModule = async (churchId: string, id: string) => {
    try {
      const response = await apiService.put<{ items: ChurchCMSBlock[] }>(
        `${API_URL}/${churchId}/custom-modules/${id}/enable`
      );
      return response.data;
    } catch (e) {
      console.error('Error enabling custom module:', e);
      throw e;
    }
  };

  disableCustomModule = async (churchId: string, id: string) => {
    try {
      const response = await apiService.put<{ items: ChurchCMSBlock[] }>(
        `${API_URL}/${churchId}/custom-modules/${id}/disable`
      );
      return response.data;
    } catch (e) {
      console.error('Error disabling custom module:', e);
      throw e;
    }
  };

  deleteCustomModule = async (churchId: string, id: string): Promise<Record<string, never>> => {
    try {
      const response = await apiService.delete<Record<string, never>>(`${API_URL}/${churchId}/custom-modules/${id}`);
      return response.data;
    } catch (e) {
      console.error('Error deleting custom module:', e);
      throw e;
    }
  };

  uploadCustomModuleImage = async (churchId: string, image: File) => {
    try {
      const response = await apiService.post<UploadFileResponse>(
        `${API_URL}/${churchId}/custom-modules/upload-image`,
        { image },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (e) {
      console.error('Error uploading custom module image:', e);
      throw e;
    }
  };

  uploadChurchImage = async (churchId: string, image: File) => {
    try {
      const response = await apiService.post<UploadFileResponse>(
        `${API_URL}/${churchId}/images/upload`,
        { image },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (e) {
      console.error('Error uploading church image:', e);
      throw e;
    }
  };

  saveChurchImage = async (churchId: string, images: ChurchImage[]) => {
    try {
      const response = await apiService.post<UploadFileResponse>(`${API_URL}/${churchId}/images/save`, {
        images,
      });
      return response.data;
    } catch (e) {
      console.error('Error saving church image:', e);
      throw e;
    }
  };

  getChurchCalendarEvents = async (churchId: string, filters: CalendarEventsFilters) => {
    try {
      const response = await apiService.get<CalendarEventOccurrence[]>(
        `${API_URL}/${churchId}/calendar-events${formatQueryString({
          ...filters,
        })}`
      );
      return response.data;
    } catch (e) {
      console.error('Error fetching church events:', e);
      throw e;
    }
  };

  connectFacebook = async (churchId: string) => {
    try {
      const response = await apiService.get(`${API_URL}/${churchId}/facebook/connect`);
      return response.data;
    } catch (e) {
      console.error('Error connecting Facebook:', e);
      throw e;
    }
  };

  disconnectFacebook = async (churchId: string) => {
    try {
      const response = await apiService.get(`${API_URL}/${churchId}/facebook/disconnect`);
      return response.data;
    } catch (e) {
      console.error('Error disconnecting Facebook:', e);
      throw e;
    }
  };

  getFacebookPages = async (churchId: string) => {
    try {
      const response = await apiService.get<FacebookPageData[]>(`${API_URL}/${churchId}/facebook/pages`);
      return response.data;
    } catch (e) {
      console.error('Error fetching Facebook pages:', e);
      throw e;
    }
  };

  submitContactForm = async (churchId: string, data: ContactFormRequestPayload) => {
    try {
      const response = await apiService.post(`${API_URL}/${churchId}/dashboard/contact/submit`, data);
      return response.data;
    } catch (e) {
      console.error('Error submitting contact form:', e);
      throw e;
    }
  };

  getGroupAdmins = async (churchId: string) => {
    try {
      const response = await apiService.get<GroupAdminAccess[]>(`/internal/churches/${churchId}/group-admins`);
      return response.data;
    } catch (e) {
      console.error('Error fetching group admins:', e);
      throw e;
    }
  };
}

export default new ChurchService();
